import { useRowSelect } from '@table-library/react-table-library/select';
import { Action, State } from '@table-library/react-table-library/types/common';
import { TableItem } from 'components/atoms';
import LabelNotificationArchived from 'components/molecules/labeledStatus/LabelNotificationArchived';
import { Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useHasPermission } from 'hooks';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useGetNotificationArchiveListInfinite } from 'services/notifications/index';
import { INotificationContent } from 'services/notifications/types/types';
import {
  formatNotificationDate,
  formatNotificationTime,
  getNotificationDateSource,
  getNotificationDisplayName,
  getNotificationNavigationPath,
  getNotificationReceivedTypeLabel,
} from 'utils/notifications/notification.utils';
import { getNodes } from 'utils/table/nodes.utils';

enum SORT_COLUMNS {
  notification,
  date,
  status,
}

const NotificationArchive = () => {
  usePermissionGuard('NotificationPermissionsLIST**');
  const hasPermission = useHasPermission();

  const { t } = useTranslation();
  const columnsVisibility = useTableColumnsVisibility(
    'NotificationsArchived',
    SORT_COLUMNS
  );
  const navigate = useNavigate();

  const { data, isLoading, fetchNextPage, isFetchingNextPage } =
    useGetNotificationArchiveListInfinite({});

  const tableData = getNodes<INotificationContent>(data?.pages.flat());

  const hasDetailPermission = hasPermission('NotificationPermissionsLIST**', 16);

  const handleNavigateClick = (item: INotificationContent): void => {
    const path = getNotificationNavigationPath(item, hasPermission);

    if (!path) {
      return;
    }

    navigate(path);
  };

  const onSelectChange = (action: Action, state: State) => {
    if (!hasDetailPermission) {
      return;
    }

    const selectedItem: INotificationContent | undefined = tableData.nodes.find(
      ({ id }) => id === state.id
    );

    if (selectedItem) {
      handleNavigateClick(selectedItem);
    }
  };

  const select = useRowSelect(tableData, {
    onChange: onSelectChange,
  });

  const tableSelect = hasDetailPermission ? select : undefined;

  return (
    <TopbarPage
      topbar={
        <TopBar
          path={'notifications'}
          title={t(`notification-archive.title.list`)}
          topbarContent={
            <Table.CustomColumnsButton
              columns={SORT_COLUMNS}
              isClear
              visibility={columnsVisibility}
              i18nextPrefix='notification.table'
              customI18next=''
            />
          }></TopBar>
      }>
      <Table
        id={columnsVisibility.tableId}
        select={tableSelect}
        data={tableData}
        loading={isLoading || isFetchingNextPage}
        onFullyScroll={fetchNextPage}
        columns={[
          {
            label: t('notification.table.notification'),
            renderCell: (item: INotificationContent) => (
              <TableItem className='gap-2'>
                <TableItem.Text text={getNotificationDisplayName(item)} />
                <TableItem.Text
                  isSupplementary
                  text={getNotificationReceivedTypeLabel(
                    t,
                    item.notificationType
                  )}
                />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.notification),
          },
          {
            label: t('notification.table.date'),
            renderCell: (item: INotificationContent) => {
              const dateSource = getNotificationDateSource(item);

              return (
                <TableItem className='gap-2'>
                  <TableItem.Text text={formatNotificationTime(dateSource)} />
                  <TableItem.Text
                    isSupplementary
                    text={formatNotificationDate(dateSource)}
                  />
                </TableItem>
              );
            },
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.date),
          },
          {
            label: '',
            renderCell: () => (
              <TableItem>
                <LabelNotificationArchived />
              </TableItem>
            ),
            hide: columnsVisibility.hide(SORT_COLUMNS.status),
          },
        ]}
        displayMode
      />
    </TopbarPage>
  );
};

export default NotificationArchive;
